/**
 * Custom Wishlist System
 * - Heart buttons on product cards and product pages
 * - localStorage for guests, server sync for logged-in customers
 * - Renders wishlist on /pages/wishlist
 */
(function() {
  'use strict';

  // Signed-in sync goes through Shopify's App Proxy: the worker takes the
  // customer from Shopify's signature, never from an email we send (F82).
  const WISHLIST_API = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'apps/pc/account/wishlist/';
  const STORAGE_KEY = 'pc_wishlist';
  // Which signed-in customer this device's local list belongs to (lowercased email).
  // Kept on logout so the same person keeps it (cart parity); a DIFFERENT account
  // signing in on this device must never adopt or upload it (F81, 2026-10-07).
  const OWNER_KEY = 'pc_wishlist_owner';
  const CONFIRMED_OWNER_KEY = 'pc_wishlist_confirmed_owner';
  const INTENT_PREFIX = 'pc_wishlist_intent:';
  // Owner value: 'id:<customer id>' when Shopify exposes it, else 'e:<hash of
  // lowercased email>' (no plain email). Same hash in snippets/oos-notify-form.liquid.
  function emailKey(email) {
    let h = 2166136261; const s = String(email || '').toLowerCase();
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    return 'e:' + h.toString(16);
  }
  function rawCustomerId() {
    const meta = document.querySelector('meta[name="customer-id"]');
    const id = meta?.content;
    return /^[1-9]\d*$/.test(String(id || '')) ? String(id) : '';
  }
  const pageCustomerId = rawCustomerId();
  function customerId() { return pageCustomerId ? 'id:' + pageCustomerId : ''; }
  // Stored as 'id:X|e:Y' (both, when the id is exposed) so a page that lacks
  // the id still matches on the email hash.
  function myOwnerKey(email) { return [customerId(), emailKey(email)].filter(Boolean).join('|'); }
  // Markers written before the id/hash format held the plain email.
  function isMine(owner, email) {
    if (!owner) return false;
    const idPart = owner.split('|').find(part => part.startsWith('id:'));
    if (idPart && customerId()) return idPart === customerId();
    const me = String(email || '').toLowerCase();
    const mine = [customerId(), emailKey(email), me].filter(Boolean);
    return owner.split('|').some(part => mine.includes(part));
  }
  function getOwner() { try { return localStorage.getItem(OWNER_KEY) || ''; } catch (e) { return ''; } }
  function setOwner(email) { try { localStorage.setItem(OWNER_KEY, myOwnerKey(email)); } catch (e) {} }

  // Escapes text AND attribute values (quotes too; the old textContent/innerHTML
  // trick left " unescaped, so a stored value could break out of an attribute).
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

  // Capture UTM params for acquisition tracking (first-touch attribution)
  function getAcquisitionData() {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source') || '';
    const medium = params.get('utm_medium') || '';
    const campaign = params.get('utm_campaign') || '';
    const landingPage = window.location.pathname;
    if (!source && !medium && !campaign) return null;
    return { utm_source: source, utm_medium: medium, utm_campaign: campaign, landing_page: landingPage };
  }

  // Get customer info from dataLayer or Shopify globals.
  // Tier 3 EMQ (2026-05-08): when a logged-in customer email is found,
  // persist it to localStorage + KV via window.pcCaptureIdentity so the
  // identity propagates to all /track and /pixel/event payloads. Helper
  // attached by snippets/funnel-tracking.liquid; absent on older theme
  // deploys, so we no-op when missing.
  function getCustomer(capture = true) {
    var email = null;
    var loggedIn = false;

    // Method 1: Check dataLayer (set in theme.liquid)
    const data = window.dataLayer?.find(d => d.customerEmail !== undefined);
    if (data && data.customerLoggedIn && data.customerEmail) {
      email = data.customerEmail;
      loggedIn = true;
    }

    // Method 2: Check Shopify global (new customer accounts)
    if (!email && window.ShopifyAnalytics?.meta?.page?.customerId) {
      const emailMeta = document.querySelector('meta[name="customer-email"]');
      if (emailMeta && emailMeta.content) {
        email = emailMeta.content;
        loggedIn = true;
      }
    }

    // Method 3: Check __st global (Shopify analytics)
    if (!email && window.__st?.cid) {
      const emailMeta = document.querySelector('meta[name="customer-email"]');
      if (emailMeta && emailMeta.content) {
        email = emailMeta.content;
        loggedIn = true;
      }
    }

    if (email) {
      try {
        if (capture && window.pcCaptureIdentity) window.pcCaptureIdentity(email, null, 'wishlist');
      } catch (e) { /* non-fatal */ }
      return { email: email, loggedIn: loggedIn };
    }
    return { email: null, loggedIn: false };
  }

  // Get wishlist from localStorage
  // Ids are compared and stored as strings: older entries hold numeric ids
  // (PDP product JSON), DOM data-product-id values are always strings.
  function productKey(id) { return String(id).replace(/^gid:\/\/shopify\/Product\//, ''); }
  function sameId(a, b) { return productKey(a) === productKey(b); }
  function validHandle(handle) { return typeof handle === 'string' && handle.length <= 200 && /^[a-zA-Z0-9._-]+$/.test(handle); }
  function validEntry(p) {
    return !!p && typeof p === 'object' && !Array.isArray(p) &&
      (p.id == null || p.id === '' ? validHandle(p.handle) :
        (typeof p.id === 'string' && p.id.trim().length > 0) || Number.isSafeInteger(p.id));
  }
  function handleEntry(p) {
    return p.id == null || p.id === '' ||
      (p.id === p.handle && !/^\d+$/.test(String(p.id)));
  }
  // Typed keys are local selectors only; Shopify still receives an empty ID.
  function entryKey(p) { return handleEntry(p) ? 'handle:' + p.handle : 'id:' + productKey(p.id); }
  function sameProduct(a, b) {
    if (!handleEntry(a) && !handleEntry(b) && sameId(a.id, b.id)) return true;
    return validHandle(a.handle) && a.handle === b.handle && (handleEntry(a) || handleEntry(b));
  }
  function normalizeList(list) {
    const normalized = list.map(p => ({ ...p, id: p.id == null ? '' : productKey(p.id) }));
    const result = [], seen = new Set();
    for (const p of normalized) {
      const key = entryKey(p);
      if (seen.has(key)) continue;
      // An ambiguous fallback must not join two different native products.
      if (handleEntry(p) && normalized.filter(q => !handleEntry(q) && sameProduct(q, p))
          .reduce((ids, q) => ids.add(productKey(q.id)), new Set()).size === 1) continue;
      seen.add(key); result.push(p);
    }
    return result;
  }
  function productSelector(value, handle) {
    if (value && typeof value === 'object') return value;
    const id = productKey(value);
    if (!validHandle(handle)) {
      const handles = new Set();
      document.querySelectorAll('.wishlist-heart[data-product-id]').forEach(btn => {
        const h = btn.getAttribute('data-product-handle');
        if (sameId(btn.getAttribute('data-product-id'), id) && validHandle(h)) handles.add(h);
      });
      if (handles.size === 1) handle = [...handles][0];
    }
    return { id, handle: validHandle(handle) ? handle : (!/^\d+$/.test(id) && validHandle(id) ? id : undefined) };
  }
  let localFallback = null;
  function getLocalWishlist() {
    if (localFallback) return localFallback.slice();
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      return Array.isArray(list) ? normalizeList(list.filter(validEntry)) : [];
    } catch (e) {
      return [];
    }
  }

  // Save wishlist to localStorage
  function saveLocalWishlist(wishlist) {
    localFallback = normalizeList(wishlist);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(localFallback)); }
    catch (e) { storageUnavailable = true; }
  }

  // Check if product is in wishlist
  function isInWishlist(productId, handle) {
    const wishlist = getLocalWishlist();
    return wishlist.some(p => sameProduct(p, productSelector(productId, handle)));
  }

  // Hearts change the local list and repaint at once; the server follows in the
  // background. One request per product is in flight; repeat toggles (mouse or
  // keyboard) only move the wanted state, and the loop converges on the last one.
  // A dispatched request can land even when its response is lost. Preserve its
  // owner-bound intent and never infer safe replay from an unlocked GET.
  const serverSync = new Map();
  let confirmed = new Map();
  let storageUnavailable = false;
  let journalUnavailable = false;
  let identityStopped = false;
  let loginRun = Promise.resolve();
  let queueTail = Promise.resolve();
  let readTail = Promise.resolve();
  let revision = 0;
  let readGeneration = 0;
  let writeGeneration = 0;
  let initialImportDone = false;
  let readUnavailable = false;
  let syncFocusAction = '';
  const pageCustomer = getCustomer();
  const pageOwner = myOwnerKey(pageCustomer.email);
  const initialOwner = getOwner();
  const initialLocal = getLocalWishlist();
  if (pageCustomer.loggedIn && initialOwner && !isMine(initialOwner, pageCustomer.email)) {
    saveLocalWishlist([]); setOwner(pageCustomer.email);
  }

  function currentOwner() {
    const owner = getOwner();
    return !identityStopped && rawCustomerId() === pageCustomerId &&
      (pageCustomer.loggedIn ? (!owner || isMine(owner, pageCustomer.email)) : owner === initialOwner) &&
      String(getCustomer(false).email || '').toLowerCase() === String(pageCustomer.email || '').toLowerCase();
  }
  function validList(list) {
    return Array.isArray(list) && list.every(validEntry);
  }
  function signedReceipt(response, result) {
    return currentOwner() && response.ok && !response.redirected && result?.success === true &&
      typeof result.customerId === 'string' && result.customerId === pageCustomerId && validList(result.wishlist);
  }
  async function verifyOwner() {
    if (!pageCustomerId || !currentOwner()) return false;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}?section_id=pc-cart-identity&pc_wishlist_identity=${Date.now()}`, {
        cache: 'no-store', signal: AbortSignal.timeout(5000)
      });
      if (!currentOwner() || !response.ok || response.redirected) return false;
      const html = await response.text();
      if (!currentOwner()) return false;
      const marker = new DOMParser().parseFromString(html, 'text/html').querySelector('meta[name="pc-cart-customer"]');
      if (!marker) return false;
      if (marker.content !== pageCustomerId) { identityStopped = true; return false; }
      return true;
    } catch (e) { return false; }
  }
  function writeIntent(record) {
    try { localStorage.setItem(record.key, JSON.stringify(record)); }
    catch (e) { storageUnavailable = true; }
  }
  function clearIntent(record) {
    try { localStorage.removeItem(record.key); } catch (e) { storageUnavailable = true; }
  }
  function newIntent(product, want) {
    const token = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { resolvePrice, ...saved } = product;
    return { key: `${INTENT_PREFIX}${pageCustomerId}:${token}`, owner: pageCustomerId,
      revision: Date.now() * 1000 + (++revision % 1000), product: saved, want, dispatched: false };
  }
  function restoreIntents() {
    if (!pageCustomerId) return;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(`${INTENT_PREFIX}${pageCustomerId}:`)) continue;
        let r;
        try { r = JSON.parse(localStorage.getItem(key)); }
        catch (e) { journalUnavailable = true; continue; }
        if (!r || r.key !== key || r.owner !== pageCustomerId || !validEntry(r.product) ||
            typeof r.want !== 'boolean' || typeof r.dispatched !== 'boolean' || !Number.isSafeInteger(r.revision)) {
          journalUnavailable = true; continue;
        }
        const id = entryKey(r.product), old = serverSync.get(id);
        if (!old) serverSync.set(id, { product: r.product, want: r.want, record: r, unknown: r.dispatched,
          uncertainProducts: r.dispatched ? [r.product] : [], run: null });
        else {
          old.unknown ||= r.dispatched && old.inFlightKey !== r.key;
          if (r.dispatched && old.inFlightKey !== r.key) old.uncertainProducts.push(r.product);
          if (r.revision > old.record.revision) { old.product = r.product; old.want = r.want; old.record = r; }
        }
      }
    } catch (e) { storageUnavailable = true; }
  }
  restoreIntents();
  function pendingState(product) {
    const exact = serverSync.get(entryKey(product));
    if (exact) return exact;
    const aliases = [...serverSync.values()].filter(s => sameProduct(s.product, product));
    return aliases.length === 1 ? aliases[0] : undefined;
  }
  function unknownAlias(product) {
    return [...serverSync.values()].some(s => (s.uncertainProducts || []).some(p => sameProduct(p, product)));
  }
  function adoptConfirmed(list) {
    confirmed = new Map(normalizeList(list).map(p => [entryKey(p), p]));
  }
  function isConfirmedIn(productIdOrHandle) {
    if (!pageCustomer.loggedIn || !pageCustomerId || !currentOwner()) return false;
    for (const p of confirmed.values()) {
      if ((sameProduct(p, productSelector(productIdOrHandle)) || p.handle === productIdOrHandle) &&
          !unknownAlias(p) && ![...serverSync.values()].some(s => sameProduct(s.product, p))) return true;
    }
    return false;
  }
  function publishChange() {
    try { document.dispatchEvent(new CustomEvent('pc:wishlist:changed')); } catch (e) {}
    showSyncStatus();
  }
  function showSyncStatus() {
    const pending = [...serverSync.values()];
    const needsReload = identityStopped || (pageCustomer.loggedIn && !pageCustomerId);
    let el = document.getElementById('pc-wishlist-sync-status');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pc-wishlist-sync-status'; el.className = 'wishlist-sync-status';
      el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');
      const host = document.querySelector('.product__info-container') || document.body;
      const grid = document.querySelector('.wishlist-widget-area');
      if (grid?.insertAdjacentElement) grid.insertAdjacentElement('beforebegin', el);
      else host.appendChild(el);
    }
    el.hidden = !pending.length && !identityStopped && !readUnavailable;
    if (el.contains?.(document.activeElement)) syncFocusAction = document.activeElement.getAttribute('data-wishlist-sync-action') || '';
    if (el.hidden) {
      if (syncFocusAction) {
        const target = document.querySelector('.wishlist-header__title') || document.querySelector('.wishlist-heart[data-product-id]');
        target?.focus?.(); syncFocusAction = '';
      }
      return;
    }
    const message = identityStopped ? 'Your signed-in account changed. Reload this page before saving your wishlist.' :
      needsReload ? 'Reload this page to confirm your signed-in account before saving your wishlist.' :
      pending.some(s => s.run) ? 'Saving your wishlist…' :
      readUnavailable && !pending.length ? 'Your account wishlist is temporarily unavailable. Your device list is kept. Check again to load your saved wishlist.' :
      `Account save not confirmed. Your change is kept ${storageUnavailable ? 'on this page' : 'on this device'}.`;
    el.textContent = message;
    if (!pending.some(s => s.run)) {
      const button = document.createElement('button'); button.type = 'button';
      button.setAttribute('data-wishlist-sync-action', 'check');
      button.textContent = needsReload ? 'Reload page' : 'Check saved wishlist';
      button.addEventListener('click', async () => {
        if (needsReload) { window.location.reload(); return; }
        button.disabled = true;
        try { await syncWishlistOnLogin({ readOnly: true }); } finally { button.disabled = false; }
      });
      el.appendChild(button);
      if (!needsReload && !journalUnavailable && pending.some(s => !s.unknown)) {
        const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry save';
        retry.setAttribute('data-wishlist-sync-action', 'retry');
        retry.addEventListener('click', async () => {
          retry.disabled = true;
          try { await syncWishlistOnLogin(); } finally { retry.disabled = false; }
        });
        el.appendChild(retry);
      }
      if (syncFocusAction) {
        el.querySelector(`[data-wishlist-sync-action="${syncFocusAction}"]`)?.focus?.(); syncFocusAction = '';
      }
    }
  }
  function paintHearts(productId) {
    const product = productSelector(productId);
    document.querySelectorAll('.wishlist-heart[data-product-id]').forEach(btn => {
      const selected = productSelector(btn.getAttribute('data-product-id'), btn.getAttribute('data-product-handle'));
      if (!sameProduct(selected, product)) return;
      const on = isInWishlist(selected);
      btn.classList.toggle('wishlist-heart--filled', on);
      const svg = btn.querySelector('svg');
      if (svg) svg.setAttribute('fill', on ? 'currentColor' : 'none');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    });
    updateWishlistCount();
    publishChange();
  }
  function addLocal(product) {
    const wishlist = getLocalWishlist();
    if (wishlist.some(p => sameProduct(p, product))) return;
    wishlist.push({
      id: product.id == null ? '' : String(product.id),
      handle: product.handle,
      title: product.title,
      brand: product.brand || '',
      price: product.price || '',
      image: product.image || '',
      url: product.url || '',
      addedAt: new Date().toISOString(),
      // Added while signed out: only these may be uploaded from an unowned
      // device list on sign-in (lists from before the owner marker existed
      // may hold another account's items).
      guest: !getCustomer().loggedIn || undefined
    });
    saveLocalWishlist(wishlist);
  }
  function removeLocal(productId) {
    const list = getLocalWishlist(), selected = productSelector(productId);
    const ambiguous = handleEntry(selected) && list.filter(p => !handleEntry(p) && sameProduct(p, selected)).length > 1;
    saveLocalWishlist(list.filter(p => ambiguous ? entryKey(p) !== entryKey(selected) : !sameProduct(p, selected)));
  }
  async function serverAdd(product, record) {
    // PDP: the price is resolved here (background), never before the heart fills.
    // Read at send time (after any variant switch) and never cached on the product.
    const { resolvePrice, ...sent } = product;
    if (typeof resolvePrice === 'function') {
      try { sent.price = (await resolvePrice()) || sent.price; } catch (e) {}
    }
    if (!currentOwner() || !await verifyOwner() || !currentOwner()) return { refused: true };
    if (journalUnavailable || unknownAlias(product)) return { refused: true };
    if (pendingState(product)?.want !== true) return { obsolete: true };
    if (product.id == null || (handleEntry(product) && String(product.id).length > 100)) sent.id = '';
    const body = { email: pageCustomer.email, customerId: pageCustomerId, product: sent };
    // Phase 1.6 CAPI dedup: pass the same event_id funnel-tracking.liquid
    // writes to sessionStorage, so when /track ALSO fires wishlist_add
    // for the same click, the worker bridge uses this same id and Meta
    // dedupes server + browser fires. Without this, both paths land at
    // Meta with different ids and the AddToWishlist event double-counts.
    try {
      var evidKey = 'pc_evid:wishlist_add:' + (product && product.handle ? product.handle : '');
      var evid = sessionStorage.getItem(evidKey);
      if (evid) body.event_id = evid;
    } catch (e) { /* sessionStorage may be blocked; bridge falls back */ }
    try { body.currency = localStorage.getItem('baCurrency') || localStorage.getItem('bacurr_currency') || ''; } catch (e) {}
    const acquisition = getAcquisitionData();
    if (acquisition) body.acquisition = acquisition;
    // Sync pending archetype from guest self-selection
    try {
      var pendingArch = JSON.parse(localStorage.getItem('pc_pending_archetype'));
      if (pendingArch && pendingArch.archetype && !pendingArch.synced && !pendingArch.wishlistTransfer) {
        // Wishlist acceptance is not an ACK for this optional background write.
        // Preserve the first owner/selection attempt and do not replay it on
        // later hearts or another account. The separate field receipt remains due.
        pendingArch.wishlistTransfer = { customerId: pageCustomerId, archetype: pendingArch.archetype,
          selectedAt: pendingArch.selectedAt || null, dispatched: true };
        localStorage.setItem('pc_pending_archetype', JSON.stringify(pendingArch));
        body.pendingArchetype = pendingArch.archetype;
      }
    } catch (e) { /* ignore */ }
    record.dispatched = true; writeIntent(record); writeGeneration++;
    const response = await fetch(`${WISHLIST_API}add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body), signal: AbortSignal.timeout(10000)
    });
    if (!currentOwner()) return null;
    const result = await response.json();
    if (response.status === 403 && result?.error === 'account_changed') {
      record.dispatched = false; writeIntent(record); identityStopped = true;
    }
    return signedReceipt(response, result) && normalizeList(result.wishlist).some(p => sameProduct(p, product)) ? result : null;
  }
  async function serverRemove(product, record) {
    if (!await verifyOwner() || !currentOwner()) return { refused: true };
    if (journalUnavailable || unknownAlias(product)) return { refused: true };
    if (pendingState(product)?.want !== false) return { obsolete: true };
    if (handleEntry(product) && [...confirmed.values()].filter(p => !handleEntry(p) && sameProduct(p, product)).length > 1) return { refused: true };
    const selector = { productId: handleEntry(product) ? product.handle : productKey(product.id) };
    if (validHandle(product.handle)) selector.productHandle = product.handle;
    if (handleEntry(product)) selector.handleOnly = true;
    record.dispatched = true; writeIntent(record); writeGeneration++;
    const response = await fetch(`${WISHLIST_API}remove`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: pageCustomer.email, customerId: pageCustomerId, ...selector, currency: (function() { try { return localStorage.getItem('baCurrency') || localStorage.getItem('bacurr_currency') || ''; } catch(e) { return ''; } })() }),
      signal: AbortSignal.timeout(10000)
    });
    if (!currentOwner()) return null;
    const result = await response.json().catch(() => null);
    if (response.status === 403 && result?.error === 'account_changed') {
      record.dispatched = false; writeIntent(record); identityStopped = true;
    }
    return signedReceipt(response, result) && !result.wishlist.some(p => sameProduct(p, product)) ? result : null;
  }
  let hasRead = false;
  function receipt(state) {
    const pending = !!state && [...serverSync.values()].includes(state);
    return { success: !pending, pending,
      reason: state?.reason || '', wishlist: getLocalWishlist() };
  }
  function applyObservedList() {
    if (!currentOwner() || !hasRead) return;
    const list = [...confirmed.values()];
    for (const s of serverSync.values()) {
      const matches = list.filter(p => sameProduct(p, s.product));
      const ambiguous = handleEntry(s.product) && matches.filter(p => !handleEntry(p)).length > 1;
      if (!s.want) for (const p of matches) {
        if (!ambiguous || entryKey(p) === entryKey(s.product)) list.splice(list.indexOf(p), 1);
      }
      else if (!matches.length) list.push({ ...s.product });
    }
    saveLocalWishlist(list); setOwner(pageCustomer.email);
  }
  function syncToServer(product, want) {
    let s = pendingState(product);
    const id = s ? [...serverSync].find(([, value]) => value === s)[0] : entryKey(product);
    if (!s) { s = { product, run: null, unknown: false, uncertainProducts: [] }; serverSync.set(id, s); }
    if (s.record && !s.record.dispatched) clearIntent(s.record);
    s.want = want;
    if (product.handle && (!handleEntry(product) || handleEntry(s.product))) s.product = product;
    s.record = newIntent(s.product, want); writeIntent(s.record);
    if (s.run) return s.run;
    s.run = queueTail.then(async () => {
      try {
        await loginRun;
        if (!currentOwner() || !pageCustomerId || !hasRead || journalUnavailable) {
          s.reason = journalUnavailable ? 'local_recovery_unavailable' : identityStopped ? 'account_changed' : 'identity_unavailable'; return receipt(s);
        }
        if (unknownAlias(s.product)) { s.reason = 'unconfirmed'; return receipt(s); }
        s.have = [...confirmed.values()].some(p => sameProduct(p, s.product));
        while (s.want !== s.have) {
          if (unknownAlias(s.product) || journalUnavailable) { s.reason = journalUnavailable ? 'local_recovery_unavailable' : 'unconfirmed'; break; }
          const target = s.want;
          const record = s.record;
          s.inFlightKey = record.key;
          let result = null;
          try { result = target ? await serverAdd(s.product, record) : await serverRemove(s.product, record); }
          catch (e) { console.error('[Wishlist] Sync error:', e); }
          if (result?.obsolete) continue;
          if (!result || result.refused) {
            s.unknown ||= record.dispatched;
            if (record.dispatched) s.uncertainProducts.push(record.product);
            s.reason = journalUnavailable ? 'local_recovery_unavailable' : identityStopped ? 'account_changed' : s.unknown ? 'unconfirmed' : 'identity_unavailable';
            break;
          }
          clearIntent(record);
          if (!currentOwner()) break;
          s.have = target;
          writeGeneration++;
          adoptConfirmed(result.wishlist.map(({ guest, ...item }) => item));
          s.reason = '';
        }
      } finally {
        s.run = null;
        s.inFlightKey = null;
        if (!s.unknown && hasRead && currentOwner() && s.want === s.have) {
          clearIntent(s.record); serverSync.delete(id);
        }
        applyObservedList(); refreshWishlistUI();
      }
      return receipt(s);
    });
    queueTail = s.run.catch(() => {});
    publishChange();
    return s.run;
  }

  // Add product to wishlist
  async function addToWishlist(product) {
    if (!currentOwner()) {
      identityStopped = true; showSyncStatus();
      return { success: false, pending: false, reason: 'account_changed', wishlist: getLocalWishlist() };
    }
    if (!isInWishlist(product)) {
      addLocal(product);
      paintHearts(product);
    }
    if (pageCustomer.loggedIn) return syncToServer(product, true);
    updateWishlistCount();
    return { success: true, wishlist: getLocalWishlist() };
  }

  // Remove product from wishlist
  async function removeFromWishlist(productId, handle) {
    if (!currentOwner()) {
      identityStopped = true; showSyncStatus();
      return { success: false, pending: false, reason: 'account_changed', wishlist: getLocalWishlist() };
    }
    const selected = productSelector(productId, handle);
    const list = normalizeList([...getLocalWishlist(), ...confirmed.values()]);
    const matches = list.filter(p => sameProduct(p, selected));
    const known = list.find(p => entryKey(p) === entryKey(selected)) || (matches.length === 1 ? matches[0] : null);
    removeLocal(known || selected);
    paintHearts(productId);
    if (pageCustomer.loggedIn) return syncToServer(known || selected, false);
    updateWishlistCount();
    return { success: true, wishlist: getLocalWishlist() };
  }

  // Toggle product in wishlist
  async function toggleWishlist(product) {
    if (isInWishlist(product)) {
      return removeFromWishlist(product);
    } else {
      return addToWishlist(product);
    }
  }

  // Update wishlist count in header
  function updateWishlistCount() {
    const count = getLocalWishlist().length;
    const countEls = document.querySelectorAll('.wishlist-count');
    countEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // Re-draw every surface that read the local list before the sync changed it,
  // so a newly signed-in customer never sees the previous owner's hearts.
  function refreshWishlistUI() {
    document.querySelectorAll('.wishlist-heart[data-product-id]').forEach(btn => {
      const on = isInWishlist(btn.getAttribute('data-product-id'), btn.getAttribute('data-product-handle'));
      btn.classList.toggle('wishlist-heart--filled', on);
      const svg = btn.querySelector('svg');
      if (svg) svg.setAttribute('fill', on ? 'currentColor' : 'none');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    });
    updateWishlistCount();
    renderWishlistPage().catch(e => console.error('[Wishlist] Render unavailable:', e));
    publishChange();
  }

  // Sync local wishlist with server on login
  function syncWishlistOnLogin(options = {}) {
    const read = ++readGeneration;
    const task = readTail.then(() => readWishlist(options, read));
    readTail = task.catch(() => {});
    return task;
  }
  async function readWishlist({ readOnly = false }, read) {
    if (!pageCustomer.loggedIn || !currentOwner()) return;
    if (!pageCustomerId) { readUnavailable = true; showSyncStatus(); return; }
    const writes = writeGeneration;
    try {
      const response = await fetch(`${WISHLIST_API}get`, { cache: 'no-store', signal: AbortSignal.timeout(5000) });
      if (!currentOwner() || read !== readGeneration || writes !== writeGeneration) return;
      const result = await response.json();
      if (!currentOwner() || read !== readGeneration || writes !== writeGeneration) return;
      if (!signedReceipt(response, result)) {
        readUnavailable = true;
        if (result?.customerId && result.customerId !== pageCustomerId) identityStopped = true;
        return;
      }
      readUnavailable = false;
      adoptConfirmed(result.wishlist);
      hasRead = true;
      if (!initialImportDone) {
        let importedBefore = false;
        try { importedBefore = localStorage.getItem(CONFIRMED_OWNER_KEY) === pageOwner; } catch (e) {}
        const local = isMine(initialOwner, pageCustomer.email) ? initialLocal :
          initialOwner ? [] : initialLocal.filter(p => p?.guest);
        for (const product of local) {
          if (![...confirmed.values()].some(p => sameProduct(p, product)) && !pendingState(product) &&
              (!importedBefore || product.guest)) {
            const record = newIntent(product, true); writeIntent(record);
            serverSync.set(entryKey(product), { want: true, product, record, unknown: false, uncertainProducts: [], run: null });
          }
        }
        try { localStorage.setItem(CONFIRMED_OWNER_KEY, pageOwner); } catch (e) { storageUnavailable = true; }
        initialImportDone = true;
      }
      applyObservedList(); refreshWishlistUI();
      if (!readOnly) {
        // Imports and hearts use the same serialized writer. Unknown dispatches
        // stay pending, even when this read observes their desired membership.
        for (const s of [...serverSync.values()]) {
          if (!s.run && !s.unknown) syncToServer(s.product, s.want);
        }
      }
    } catch (e) {
      if (currentOwner() && read === readGeneration && writes === writeGeneration) readUnavailable = true;
      console.error('Wishlist sync error:', e);
    }
    finally { showSyncStatus(); }
  }

  // Create heart button HTML
  function createHeartButton(productId, filled, handle) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'wishlist-heart' + (filled ? ' wishlist-heart--filled' : '');
    btn.setAttribute('data-product-id', productId);
    if (validHandle(handle)) btn.setAttribute('data-product-handle', handle);
    btn.setAttribute('aria-label', filled ? 'Remove from wishlist' : 'Add to wishlist');
    btn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="${filled ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    `;
    return btn;
  }

  // Extract product data from card element
  function getProductDataFromCard(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;

    let url = link.getAttribute('href');
    const handleMatch = url.match(/\/products\/([^?#]+)/);
    if (!handleMatch) return null;

    const handle = handleMatch[1];

    // Clean title - remove extra whitespace and newlines
    const titleEl = card.querySelector('.card__heading a, .card__heading');
    const title = titleEl?.textContent?.replace(/\s+/g, ' ').trim() || '';

    // Get vendor/brand - try multiple selectors
    let vendor = '';
    const vendorEl = card.querySelector('.card__subtitle, [class*="vendor"], .product-vendor');
    if (vendorEl) {
      vendor = vendorEl.textContent?.replace(/\s+/g, ' ').trim() || '';
    }
    // Fallback: try caption but filter out size/type info
    if (!vendor) {
      const caption = card.querySelector('.caption-with-letter-spacing');
      if (caption) {
        const text = caption.textContent?.replace(/\s+/g, ' ').trim() || '';
        // Only use if it looks like a brand (not size like "50ml" or type like "Elixir")
        if (text && !text.match(/^\d+ml$/i) && text.length > 2) {
          vendor = text;
        }
      }
    }

    // Get price - clean up formatting while preserving the displayed currency
    const priceEl = card.querySelector('.price-item--regular, .price-item--sale');
    let price = priceEl?.textContent?.replace(/\s+/g, ' ').trim() || '';
    // Extract currency symbol/code and numeric value, then reconstruct cleanly
    // Matches: $, €, £, AED, Dhs, USD, EUR, GBP, etc. followed by number
    const currencyMatch = price.match(/^([^\d]*?)\s*([\d,]+\.?\d*)/);
    if (currencyMatch) {
      let currency = currencyMatch[1].trim();
      const amount = currencyMatch[2];
      // Normalize common currency variations
      if (/dhs\.?/i.test(currency)) currency = 'AED';
      price = currency + ' ' + amount;
    }

    const img = card.querySelector('.card__media img');
    // Get real image URL - prefer srcset (higher res) or data-src over src (may be lazy placeholder)
    let image = '';
    if (img) {
      // Try srcset first (usually has the real CDN URL)
      if (img.srcset) {
        const srcsetParts = img.srcset.split(',');
        if (srcsetParts.length > 0) {
          image = srcsetParts[0].trim().split(' ')[0];
        }
      }
      // Fallback to data-src (lazy loading)
      if (!image && img.dataset.src) {
        image = img.dataset.src;
      }
      // Final fallback to src
      if (!image) {
        image = img.src || '';
      }
    }

    // Get product ID from quick-add form or data attribute
    let productId = '';

    // Try to extract from modal ID
    const modal = card.querySelector('[id^="QuickAdd-"]');
    if (modal) {
      const match = modal.id.match(/QuickAdd-(\d+)/);
      if (match) productId = match[1];
    }

    // Fallback: use handle as ID (will need to fetch actual ID)
    if (!productId) productId = handle;

    // Build URL from handle - always construct clean URL to avoid doubling issues
    const fullUrl = 'https://parfumcentral.com/products/' + handle;

    const extractedData = {
      id: productId,
      handle,
      title,
      brand: vendor,
      price,
      image,
      url: fullUrl
    };

    // DEBUG: Log extracted data

    return extractedData;
  }

  // Inject heart buttons into product cards
  function injectHeartButtons() {
    const cards = document.querySelectorAll('.card-wrapper:not(.wishlist-heart-added)');

    cards.forEach(card => {
      const product = getProductDataFromCard(card);
      if (!product) return;

      const cardInner = card.querySelector('.card__inner');
      if (!cardInner) return;

      card.classList.add('wishlist-heart-added');

      const filled = isInWishlist(product);
      const btn = createHeartButton(product.id, filled, product.handle);

      // The heart repaints inside toggleWishlist before any request.
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleWishlist(product);
      });

      cardInner.appendChild(btn);
    });
  }

  // Inject heart button on product page
  function injectProductPageHeart() {
    const productSection = document.querySelector('.product');
    if (!productSection) return;
    if (document.querySelector('.product .wishlist-heart')) return;

    const form = productSection.querySelector('form[action*="/cart/add"]');
    const variantInput = form?.querySelector('input[name="id"]');
    const productJson = document.querySelector('[data-product-json]');

    let productId = '';
    let handle = '';
    let vendor = '';

    // Clean title - get from h1 specifically, remove extra whitespace
    let titleEl = document.querySelector('.product__title h1');
    if (!titleEl) titleEl = document.querySelector('.product__title');
    let title = titleEl?.innerText?.replace(/\s+/g, ' ').trim() || '';
    // Remove duplicate title (some themes show title twice)
    const halfLen = Math.floor(title.length / 2);
    if (title.length > 10 && title.substring(0, halfLen).trim() === title.substring(halfLen).trim()) {
      title = title.substring(0, halfLen).trim();
    }

    // Price will be fetched from AJAX API at click time (not from Nova-converted DOM)
    let price = '';

    let image = productSection.querySelector('.product__media img')?.src || '';

    // Get handle from URL
    const urlMatch = window.location.pathname.match(/\/products\/([^?#]+)/);
    if (urlMatch) handle = urlMatch[1];

    // Get product ID and vendor from product JSON (most reliable source)
    if (productJson) {
      try {
        const data = JSON.parse(productJson.textContent);
        productId = data.id != null ? String(data.id) : '';
        vendor = data.vendor || '';
      } catch (e) {}
    }

    // Fallback vendor from DOM if not in JSON
    if (!vendor) {
      // Try meta tag first
      const vendorMeta = document.querySelector('meta[property="product:brand"], meta[name="product:brand"]');
      if (vendorMeta) {
        vendor = vendorMeta.content || '';
      }
    }
    if (!vendor) {
      // Try DOM element
      const vendorEl = productSection.querySelector('.product__text.caption-with-letter-spacing');
      if (vendorEl) {
        const text = vendorEl.textContent?.replace(/\s+/g, ' ').trim() || '';
        // Only use if it doesn't look like a product type
        if (text && !/eau de (parfum|toilette)|edp|edt|perfume|cologne/i.test(text)) {
          vendor = text;
        }
      }
    }

    // Fallback to variant input
    if (!productId && variantInput) {
      productId = variantInput.dataset.productId || handle;
    }

    if (!productId && !handle) return;

    // Canonical AED price of the selected variant, from the page (Liquid
    // renders the store-currency variant, not the Nova-converted DOM); the
    // AJAX product JSON is only a fallback, fetched in the background.
    const selectedPrice = () => {
      try {
        const v = JSON.parse(productSection.querySelector('[data-selected-variant]')?.textContent || 'null');
        if (v && v.price != null) return 'AED ' + (Number(v.price) / 100).toFixed(2);
      } catch (e) {}
      return '';
    };
    const product = {
      id: productId || handle,
      handle,
      title,
      brand: vendor,
      price,
      image,
      url: 'https://parfumcentral.com/products/' + handle,
      resolvePrice: async () => {
        const fromPage = selectedPrice();
        if (fromPage || !handle) return fromPage;
        const prodData = await (await fetch(`/products/${handle}.json`)).json();
        const selectedVariantId = document.querySelector('input[name="id"]')?.value || '';
        const variant = selectedVariantId
          ? prodData.product.variants.find(v => String(v.id) === selectedVariantId)
          : prodData.product.variants[0];
        const aedPrice = variant?.price || prodData.product.variants[0]?.price || '0';
        return 'AED ' + parseFloat(aedPrice).toFixed(2);
      }
    };

    // DEBUG: Log product page extraction

    const filled = isInWishlist(product);
    const btn = createHeartButton(product.id, filled, product.handle);
    btn.classList.add('wishlist-heart--product-page');

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      toggleWishlist(product);
    });

    // Build a row that holds the wishlist heart plus a scroll-to-info CTA.
    // The CTA encourages visitors to scroll down to the editorial description
    // section where notes, brand story, and signature context live.
    const actionsRow = document.createElement('div');
    actionsRow.className = 'product-actions-row';
    actionsRow.appendChild(btn);

    const scrollCta = document.createElement('button');
    scrollCta.type = 'button';
    scrollCta.className = 'product-scroll-cta';
    scrollCta.setAttribute('aria-label', 'Scroll to fragrance details');
    scrollCta.innerHTML = '<span class="product-scroll-cta__text">See if it matches <em class="product-scroll-cta__em">your style</em></span><svg class="product-scroll-cta__chev" viewBox="0 0 14 8" fill="none" aria-hidden="true" focusable="false"><path d="M1 1L7 7L13 1" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    scrollCta.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector('.product__description')
        || document.querySelector('.pc-description')
        || document.querySelector('product-info + .shopify-section')
        || document.querySelector('#shopify-section-template--' + (document.querySelector('[id^="shopify-section-template-"][id$="__rich_text_nGMcJ7"]')?.id?.match(/template--(\d+)/)?.[1] || '') + '__rich_text_nGMcJ7');
      if (target) {
        const headerOffset = document.querySelector('.header-wrapper')?.offsetHeight || 80;
        const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
    actionsRow.appendChild(scrollCta);

    // Insert the row after product title (or before add to cart as fallback)
    const titleElPos = productSection.querySelector('.product__title');
    if (titleElPos) {
      titleElPos.parentNode.insertBefore(actionsRow, titleElPos.nextSibling);
    } else if (form) {
      form.parentNode.insertBefore(actionsRow, form);
    }

    // Reveal the underline when the CTA enters the viewport (one-shot).
    // If IO is unavailable, reveal immediately. Reduced-motion users get
    // the revealed state via CSS without depending on this observer.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            scrollCta.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });
      io.observe(scrollCta);
    } else {
      scrollCta.classList.add('is-revealed');
    }
  }

  // Render wishlist on wishlist page
  // ponytail: per-page memo. The post-sync redraw and remove re-renders reuse
  // the first fetch instead of refetching every product (was 44 requests for 22
  // items). Network failures are not cached, so a later render retries them.
  const productCache = new Map();
  function memo(key, load) {
    if (!productCache.has(key)) {
      productCache.set(key, load().then(p => {
        if (!p) productCache.delete(key); // network error: retry next render
        return p;
      }));
    }
    return productCache.get(key);
  }
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function fetchProductData(handle) {
    return memo('p:' + handle, () => fetchProductDataUncached(handle));
  }
  // Live product from the AJAX endpoint (/products/<handle>.js: fastest single
  // fetch and carries `available`). Returns { gone: true } on a 404 (deleted),
  // or null on a network/other error (not cached; the next render retries).
  async function fetchProductDataUncached(handle) {
    try {
      const response = await fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`);
      if (response.status === 404) return { gone: true };
      if (!response.ok) return null;
      return (await response.json()) || null;
    } catch (e) {
      console.error('Failed to fetch product:', handle, e);
      return null;
    }
  }
  // Closest buyable alternative for an unavailable product, rendered by the
  // SAME Liquid as the sold-out PDP line (snippets/pc-closest-alt.liquid via
  // sections/pc-closest-alt.liquid, Section Rendering API). Fetched only for
  // unavailable (not deleted) items; empty metafield → nothing.
  function fetchClosestAlternative(handle) {
    return memo('a:' + handle, async () => {
      try {
        const r = await fetch(`${ROOT}products/${encodeURIComponent(handle)}?section_id=pc-closest-alt`);
        if (!r.ok) return null;
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        const line = doc.querySelector('.pc-closest');
        return line ? { html: line.outerHTML } : { none: true };
      } catch (e) { return null; }
    });
  }

  // Brand collection for a deleted item's "More from {Brand}" link: one HEAD
  // per brand (memoized); omitted unless the collection exists.
  function brandHandle(name) {
    return String(name || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function fetchBrandCollection(name) {
    const handle = brandHandle(name);
    if (!handle) return Promise.resolve(null);
    return memo('b:' + handle, async () => {
      try {
        const r = await fetch(`${ROOT}collections/${handle}`, { method: 'HEAD' });
        return r.ok ? { handle, name: String(name) } : { none: true };
      } catch (e) { return null; }
    }).then(b => (b && !b.none ? b : null));
  }

  // Only same-origin /products/ paths may become links (no javascript: or
  // off-site URLs from a stored item).
  function safeProductPath(item) {
    const u = typeof item.url === 'string' ? item.url : '';
    if (/^\/products\/[A-Za-z0-9\-_.%]+(\?[A-Za-z0-9\-_.%=&]*)?$/.test(u)) return u;
    const h = String(item.handle || '');
    return /^[A-Za-z0-9\-_.]+$/.test(h) ? '/products/' + h : null;
  }

  // Store money format: the Liquid-rendered sample on the wishlist container
  // (same approach as snippets/worker-recommendations.liquid), so Nova sees the
  // markup it expects; falls back to the previous format.
  function money(cents) {
    const num = Number(cents) / 100;
    if (!isFinite(num)) return '';
    const area = document.querySelector('.wishlist-widget-area');
    const sample = area && area.getAttribute('data-money-sample');
    const sampleAmount = area && area.getAttribute('data-money-sample-amount');
    const formatted = num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (sample && sampleAmount && sample.indexOf(sampleAmount) !== -1) {
      const out = sample.replace(sampleAmount, formatted);
      return /class=["']money["']/.test(out) ? out : `<span class="money">${out}</span>`;
    }
    return `<span class="money">Dhs.${num.toFixed(2)}</span>`;
  }

  // Availability = product.available only. No stock wording at all for
  // available items (the price + button say it), and never out of stock /
  // sold out for unavailable ones (operator 2026-09-28): those get the closest
  // alternative line instead.
  function getStockStatus(product) {
    if (!product) return { available: true, text: '', class: '' };
    return product.available === false
      ? { available: false, text: '', class: 'unavailable' }
      : { available: true, text: '', class: 'available' };
  }

  // Format price (AJAX .js prices are in cents)
  function formatPrice(product) {
    if (!product || !product.variants) return '';
    const variant = product.variants[0];
    const price = Number(variant.price);
    const comparePrice = variant.compare_at_price ? Number(variant.compare_at_price) : null;
    let html = `<span class="wishlist-item__price-current">${money(price)}</span>`;
    if (comparePrice && comparePrice > price) {
      const discount = Math.round((1 - price / comparePrice) * 100);
      html = `
        <span class="wishlist-item__price-compare">${money(comparePrice)}</span>
        <span class="wishlist-item__price-current wishlist-item__price-sale">${money(price)}</span>
        <span class="wishlist-item__price-badge">-${discount}%</span>
      `;
    }
    if (variant.unit_price) {
      const unitLabel = variant.unit_price_measurement?.reference_unit || 'ml';
      html += `<span class="wishlist-item__unit-price">${money(variant.unit_price)} / ${esc(unitLabel)}</span>`;
    }
    return html;
  }

  // Approved wording (cart recovery, 2026-10-06; same as the sold-out PDP line):
  // "Closest in our range: {title}, {price}".
  function closestLine(alt) {
    return alt && alt.html ? `<div class="wishlist-item__closest">${alt.html}</div>` : '';
  }

  const BLANK_IMG = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
  // Neutral placeholder (replaces the old ChatGPT-named PNG fallback).
  const NEUTRAL_IMG = "data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20300%20300%22%3E%3Crect%20width%3D%22300%22%20height%3D%22300%22%20fill%3D%22rgb(250%2C247%2C240)%22%2F%3E%3C%2Fsvg%3E";
  // Sized supplier image (images[0] / featured_image; never the _s1_sq editorial).
  function sizedImage(url) {
    if (!url) return '';
    const u = String(url).startsWith('//') ? 'https:' + url : String(url);
    if (!/^https:\/\//.test(u)) return '';
    return /[?&]width=/.test(u) ? u : u + (u.includes('?') ? '&' : '?') + 'width=533';
  }

  // Generate share URLs
  function getShareUrls(product, url) {
    const fullUrl = 'https://parfumcentral.com' + url;
    const text = encodeURIComponent(`Check out ${product.title} on Parfum Central!`);
    const emailSubject = encodeURIComponent(`Check out ${product.title} on Parfum Central`);
    const emailBody = encodeURIComponent(`I thought you might like this fragrance:\n\n${product.title}\n${fullUrl}`);
    return {
      whatsapp: `https://wa.me/?text=${text}%20${encodeURIComponent(fullUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(fullUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
      email: `mailto:?subject=${emailSubject}&body=${emailBody}`,
      copy: fullUrl
    };
  }

  // Only the latest render may paint: the first render awaits product fetches
  // and could otherwise finish after the post-sync redraw (F81 review R1).
  let renderSeq = 0;
  let latestRender = Promise.resolve();
  function renderWishlistPage() {
    latestRender = renderWishlistPageNow();
    return latestRender;
  }
  // Resolves once no newer render has started (a render begun mid-Remove wins).
  async function settleRenders() {
    let p;
    do { p = latestRender; await p; } while (p !== latestRender);
  }
  async function renderWishlistPageNow() {
    const seq = ++renderSeq;
    const container = document.querySelector('.wishlist-widget-area');
    if (!container) return;
    if (!window.location.pathname.includes('wishlist')) return;

    const wishlist = getLocalWishlist();

    if (wishlist.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty">
          <svg class="wishlist-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <h2 class="wishlist-empty__title">Your Wishlist is Empty</h2>
          <p class="wishlist-empty__text">Save your favorite fragrances by clicking the heart icon on any product.</p>
          <a href="/collections/all-products" class="wishlist-empty__cta">Start Exploring</a>
        </div>
      `;
      announce(container, 'Your Wishlist is Empty');
      return;
    }

    // Fetch live product data for all items. Cards paint at once from the
    // stored title/image; price and stock wait for live data (never the stored
    // price, which can be stale).
    const productDataPromises = wishlist.map(item => fetchProductData(item.handle).then(product => {
      if (product && product.gone) {
        return fetchBrandCollection(item.brand).then(brand => ({ ...product, brand }));
      }
      if (!product || product.available !== false) return product;
      return fetchClosestAlternative(item.handle).then(alt => ({ ...product, closest: alt }));
    }));
    paintWishlistGrid(container, wishlist, wishlist.map(() => null), true);
    const productDataResults = await Promise.all(productDataPromises);
    if (seq !== renderSeq) return;
    paintWishlistGrid(container, wishlist, productDataResults, false);
  }

  // One polite live region outside the grid (the grid is replaced on every
  // paint). It is inserted empty first and the text set on the next frame, so
  // screen readers announce the change.
  function announce(container, text) {
    let el = container.previousElementSibling;
    if (!el || !el.classList || !el.classList.contains('wishlist-status')) {
      el = document.createElement('span');
      el.className = 'visually-hidden wishlist-status';
      el.setAttribute('role', 'status');
      container.insertAdjacentElement && container.insertAdjacentElement('beforebegin', el);
    }
    setTimeout(() => { el.textContent = text; }, 50);
  }

  function paintWishlistGrid(container, wishlist, productDataResults, pending) {

    // Render product grid. While live data loads the grid is aria-busy, with an
    // off-screen status for screen readers.
    let html = pending ? '<div class="wishlist-grid" aria-busy="true">' : '<div class="wishlist-grid">';

    for (let i = 0; i < wishlist.length; i++) {
      const item = wishlist[i];
      const product = productDataResults[i];
      const gone = !pending && !!(product && product.gone);
      const live = !pending && product && !product.gone ? product : null;
      const failed = !pending && !product;
      // Approved copy (developer, 2026-10-07): 'No longer available' (+ 'More from {Brand}'),
      // "Couldn't load the price" + 'Try again'.
      const stock = pending ? { available: true, text: '', class: 'pending' }
        : gone ? { available: false, text: 'No longer available', class: 'gone' }
        : failed ? { available: true, text: '', class: 'unknown' }
        : getStockStatus(live);
      // Price and stock come only from live product data. The stored item price
      // can be stale (price changes, Nova display), so it is never painted.
      const priceHtml = pending ? '<span class="wishlist-item__price-pending">&nbsp;</span>'
        : live ? formatPrice(live)
        : failed ? '<span class="wishlist-item__price-unavailable">Couldn&#39;t load the price</span>'
        : '';
      const safePath = gone ? null : safeProductPath(item);
      const productUrl = safePath || '#';
      const shareUrls = getShareUrls(item, productUrl);
      // Live featured image first (no extra request: it's in the product data);
      // the stored image next; the logo only when neither exists. While pending
      // with no stored image, a neutral blank avoids flashing the logo.
      const liveImage = live && sizedImage(live.featured_image || (live.images && live.images[0]));
      const imageUrl = liveImage || sizedImage(item.image) || (pending ? BLANK_IMG : NEUTRAL_IMG);
      const closest = live && live.available === false ? closestLine(product.closest) : '';
      const idAttr = esc(String(item.id));
      const handleAttr = validHandle(item.handle) ? esc(item.handle) : '';

      html += `
        <div class="wishlist-item${gone ? ' wishlist-item--gone' : ''}" data-product-id="${idAttr}">
          <button type="button" class="wishlist-item__remove" data-product-id="${idAttr}" data-product-handle="${handleAttr}" aria-label="Remove from wishlist">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          ${safePath ? `<a href="${esc(safePath)}" class="wishlist-item__link">` : '<div class="wishlist-item__link">'}
            <div class="wishlist-item__image">
              <img src="${esc(imageUrl)}" alt="${esc(item.title)}" loading="lazy" onerror="this.onerror=null;this.src='data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20300%20300%22%3E%3Crect%20width%3D%22300%22%20height%3D%22300%22%20fill%3D%22rgb(250%2C247%2C240)%22%2F%3E%3Cg%20transform%3D%22translate(150%2C150)%22%3E%3Ccircle%20r%3D%2240%22%20fill%3D%22none%22%20stroke%3D%22rgb(139%2C106%2C42)%22%20stroke-opacity%3D%220.25%22%20stroke-width%3D%221.5%22%2F%3E%3Ctext%20y%3D%227%22%20text-anchor%3D%22middle%22%20font-family%3D%22Georgia%2Cserif%22%20font-size%3D%2216%22%20font-style%3D%22italic%22%20fill%3D%22rgb(139%2C106%2C42)%22%20fill-opacity%3D%220.55%22%3EImage%20unavailable%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fsvg%3E';this.style.objectFit='contain';">
            </div>
            <div class="wishlist-item__info">
              <h3 class="wishlist-item__title">${esc(item.title)}</h3>
              ${item.brand ? `<p class="wishlist-item__brand">${esc(item.brand)}</p>` : ''}
              <div class="wishlist-item__price">${priceHtml}</div>
              <p class="wishlist-item__stock wishlist-item__stock--${stock.class}">${stock.text}</p>
            </div>
          ${safePath ? '</a>' : '</div>'}
          ${closest}
          ${failed ? '<div class="wishlist-item__closest"><button type="button" class="wishlist-item__retry">Try again</button></div>' : ''}
          ${gone && product.brand ? `<div class="wishlist-item__closest"><a href="${ROOT}collections/${product.brand.handle}" class="link underlined-link">More from ${esc(product.brand.name)}</a></div>` : ''}
          ${safePath ? `<div class="wishlist-item__share">
            <a href="${shareUrls.whatsapp}" target="_blank" rel="noopener" class="wishlist-share-btn wishlist-share-btn--whatsapp" aria-label="Share on WhatsApp">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
            <a href="${shareUrls.email}" class="wishlist-share-btn wishlist-share-btn--email" aria-label="Share via Email">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
            </a>
            <a href="${shareUrls.facebook}" target="_blank" rel="noopener" class="wishlist-share-btn wishlist-share-btn--facebook" aria-label="Share on Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="${shareUrls.twitter}" target="_blank" rel="noopener" class="wishlist-share-btn wishlist-share-btn--twitter" aria-label="Share on X">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <button type="button" class="wishlist-share-btn wishlist-share-btn--copy" data-url="${shareUrls.copy}" aria-label="Copy link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            </button>
          </div>
          <a href="${esc(safePath)}" class="wishlist-item__cta">${stock.available ? 'View Product' : 'View Details'}</a>` : ''}
        </div>
      `;
    }

    html += '</div>';
    container.innerHTML = html;
    announce(container, pending ? 'Loading prices' : 'Loaded');

    // Let Nova Multi-Currency convert the prices once live prices exist. Nova
    // only converts visible elements, so fire guarded rePeat() staggered across
    // the card fade-in (same pattern as assets/cart-drawer.js).
    if (!pending) {
      const reconvertCurrency = () => {
        if (window.baCurr && typeof window.baCurr.rePeat === 'function') {
          try { window.baCurr.rePeat(); } catch (e) {}
        }
      };
      requestAnimationFrame(reconvertCurrency);
      setTimeout(reconvertCurrency, 200);
      setTimeout(reconvertCurrency, 500);
      setTimeout(reconvertCurrency, 900);
    }

    // Add remove handlers
    container.querySelectorAll('.wishlist-item__remove').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const productId = btn.dataset.productId;
        const cards = [...container.querySelectorAll('.wishlist-item')];
        const idx = cards.indexOf(btn.closest('.wishlist-item'));
        try {
          await removeFromWishlist(productId, btn.dataset.productHandle);
          renderWishlistPage();
          await settleRenders();
        } catch (err) {
          console.error('Wishlist remove/render failed:', err);
        }
        // Keep keyboard focus in the list: the next card's first control, the
        // previous one at the end, or the page heading when the list is empty.
        const after = container.querySelectorAll('.wishlist-item');
        const card = after[idx] || after[after.length - 1];
        const target = card ? card.querySelector('button, a') : document.querySelector('.wishlist-header__title');
        if (target) {
          if (!card && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          try { target.focus(); } catch (err) {}
        }
      });
    });

    // Retry a card whose live data failed (network error; not cached)
    container.querySelectorAll('.wishlist-item__retry').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); renderWishlistPage(); });
    });

    // Add copy link handlers
    container.querySelectorAll('.wishlist-share-btn--copy').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const url = btn.dataset.url;
        try {
          await navigator.clipboard.writeText(url);
          btn.classList.add('copied');
          setTimeout(() => btn.classList.remove('copied'), 2000);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
      });
    });
  }

  // Inject CSS for wishlist heart buttons
  function injectStyles() {
    if (document.getElementById('custom-wishlist-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-wishlist-styles';
    style.textContent = `
      .wishlist-sync-status:not([hidden]) {
        margin: 1.4rem 0; padding: 1.2rem; border: 1px solid rgba(191,155,48,.35);
        border-radius: 6px; font-size: 1.3rem; line-height: 1.5; background: #faf8f3;
      }
      .wishlist-sync-status button {
        margin: .6rem .8rem 0 0; padding: .6rem 1rem; font: inherit;
        color: inherit; background: #fff; border: 1px solid currentColor; border-radius: 4px; cursor: pointer;
      }
      .wishlist-sync-status button:focus-visible { outline: 2px solid #bf9b30; outline-offset: 3px; }
      .wishlist-sync-status button:disabled { opacity: .6; cursor: wait; }
      /* Heart button on cards */
      .wishlist-heart {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
        width: 36px;
        height: 36px;
        padding: 6px;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: #bf9b30;
        transition: all 0.25s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .wishlist-heart:hover {
        background: #fff;
        transform: scale(1.1);
        box-shadow: 0 2px 8px rgba(0,0,0,0.12);
      }

      .wishlist-heart svg {
        width: 20px;
        height: 20px;
        transition: fill 0.2s ease, transform 0.2s ease;
      }

      .wishlist-heart--filled svg {
        fill: #bf9b30;
      }

      /* Product actions row: wishlist pill + editorial scroll link */
      .product-actions-row {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin: 1rem 0 1.25rem;
        align-items: flex-start;
      }

      @media screen and (min-width: 750px) {
        .product-actions-row {
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;
          gap: 20px;
        }
      }

      /* Heart button on product page */
      .wishlist-heart--product-page {
        position: relative;
        top: auto;
        right: auto;
        width: auto;
        height: auto;
        padding: 10px 20px;
        background: transparent;
        border: 1px solid rgba(191, 155, 48, 0.3);
        border-radius: 4px;
        margin: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 14px;
        letter-spacing: 0.05em;
        flex: 0 0 auto;
      }

      .product-actions-row .wishlist-heart--product-page {
        flex: 1 1 auto;
        width: 100%;
      }

      @media screen and (min-width: 750px) {
        .product-actions-row .wishlist-heart--product-page {
          flex: 0 0 auto;
          width: auto;
        }
      }

      /* Editorial scroll-to-info CTA - distinct from wishlist pill */
      .product-scroll-cta {
        position: relative;
        padding: 6px 2px 14px;
        background: transparent;
        border: 0;
        color: rgba(var(--color-foreground), 0.92);
        cursor: pointer;
        display: inline-flex;
        align-items: baseline;
        gap: 10px;
        text-align: left;
        font-family: inherit;
        line-height: 1.25;
        flex: 0 0 auto;
        appearance: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Hairline gold rule beneath text, animates in on load */
      .product-scroll-cta::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 4px;
        height: 1px;
        width: 100%;
        background: #bf9b30;
        transform-origin: left center;
        transform: scaleX(0);
        transition: transform 800ms cubic-bezier(0.22, 1, 0.36, 1) 200ms,
                    height 220ms ease,
                    background-color 220ms ease;
      }

      .product-scroll-cta.is-revealed::after {
        transform: scaleX(1);
      }

      .product-scroll-cta:hover::after {
        height: 1.5px;
        background: #a8861f;
      }

      .product-scroll-cta__text {
        font-size: 11px;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        font-weight: 500;
      }

      .product-scroll-cta__em {
        font-family: var(--font-heading-family, Georgia, 'Times New Roman', serif);
        font-style: italic;
        font-weight: 400;
        font-size: 14px;
        letter-spacing: 0.01em;
        text-transform: none;
        margin-left: 4px;
        color: #bf9b30;
      }

      .product-scroll-cta__chev {
        width: 12px;
        height: 7px;
        flex: 0 0 12px;
        stroke: #bf9b30;
        align-self: center;
        margin-bottom: 2px;
        animation: pc-cta-chev-bob 2.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
        transform-origin: center;
      }

      @keyframes pc-cta-chev-bob {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.85; }
        30%           { transform: translateY(4px); opacity: 1; }
      }

      .product-scroll-cta:hover .product-scroll-cta__chev {
        animation-duration: 0.9s;
      }

      .product-scroll-cta:focus-visible {
        outline: 1px dashed #bf9b30;
        outline-offset: 6px;
      }

      .product-scroll-cta:active {
        transform: translateY(1px);
      }

      @media (prefers-reduced-motion: reduce) {
        .product-scroll-cta::after {
          transform: scaleX(1) !important;
          transition: height 220ms ease, background-color 220ms ease;
        }
        .product-scroll-cta__chev {
          animation: none;
        }
      }

      @media screen and (min-width: 750px) {
        .product-scroll-cta {
          margin-left: 4px;
        }
      }

      .wishlist-heart--product-page::after {
        content: 'Add to Wishlist';
        color: #bf9b30;
      }

      .wishlist-heart--product-page.wishlist-heart--filled::after {
        content: 'Remove from Wishlist';
      }

      .wishlist-heart--product-page:hover {
        background: rgba(191, 155, 48, 0.08);
        border-color: #bf9b30;
        transform: none;
        box-shadow: none;
      }

      .wishlist-heart--product-page svg {
        width: 18px;
        height: 18px;
      }

      /* Card positioning */
      .card__inner {
        position: relative;
      }

      /* Wishlist page grid */
      .wishlist-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
      }

      @media screen and (min-width: 750px) {
        .wishlist-grid {
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }
      }

      @media screen and (min-width: 990px) {
        .wishlist-grid {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      .wishlist-item {
        position: relative;
        background: #fff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      }

      .wishlist-item:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.1);
      }

      .wishlist-item__remove {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
        width: 28px;
        height: 28px;
        padding: 0;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: #666;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.25s ease;
      }

      .wishlist-item__remove:hover {
        background: #bf9b30;
        color: #fff;
      }

      .wishlist-item__remove svg {
        width: 14px;
        height: 14px;
      }

      .wishlist-item__link {
        display: block;
        text-decoration: none;
        color: inherit;
      }

      .wishlist-item__image {
        aspect-ratio: 1;
        overflow: hidden;
        background: #f9f9f9;
      }

      .wishlist-item__image img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: transform 0.4s ease;
      }

      .wishlist-item:hover .wishlist-item__image img {
        transform: scale(1.05);
      }

      .wishlist-item__info {
        padding: 1rem;
        text-align: center;
      }

      .wishlist-item__title {
        font-family: var(--font-heading-family);
        font-size: 1.05rem;
        font-weight: 500;
        color: #1a1a1a;
        margin: 0 0 0.4rem;
        line-height: 1.35;
      }

      .wishlist-item__brand {
        font-size: 0.9rem;
        color: #666;
        margin: 0 0 0.6rem;
        letter-spacing: 0.03em;
      }

      .wishlist-item__closest { font-size: 0.85rem; margin: 0 1rem 0.75rem; text-align: center; }
      .wishlist-item__stock { min-height: 1.4em; }
      .wishlist-item__price-unavailable { font-size: 0.85rem; font-weight: 400; color: #6b6b6b; }
      .wishlist-item__retry { background: none; border: 0; padding: 0; font: inherit; font-size: 0.85rem; text-decoration: underline; cursor: pointer; color: inherit; }
      .wishlist-item--gone .wishlist-item__image img { opacity: 0.45; }
      .wishlist-item__price-pending {
        display: inline-block;
        width: 5.5em;
        height: 1.1em;
        border-radius: 2px;
        background: rgba(0, 0, 0, 0.06);
      }
      .wishlist-item__price {
        font-size: 1.1rem;
        font-weight: 600;
        color: #1a1a1a;
        margin: 0 0 0.5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 3.4em;
        gap: 4px;
      }

      .wishlist-item__price-current {
        color: #bf9b30;
        font-size: 1.2rem;
      }

      .wishlist-item__price-sale {
        color: #c41e3a;
      }

      .wishlist-item__price-compare {
        color: #999;
        font-size: 0.9rem;
        text-decoration: line-through;
        font-weight: 400;
      }

      .wishlist-item__price-badge {
        background: #c41e3a;
        color: #fff;
        font-size: 0.7rem;
        padding: 2px 6px;
        border-radius: 3px;
        font-weight: 600;
      }

      .wishlist-item__unit-price {
        font-size: 0.85rem;
        color: #666;
        font-weight: 400;
        background: #f5f5f5;
        padding: 3px 8px;
        border-radius: 3px;
        margin-top: 4px;
      }

      .wishlist-item__stock {
        font-size: 0.85rem;
        font-weight: 500;
        margin: 0.5rem 0 0;
        padding: 4px 10px;
        border-radius: 4px;
        display: inline-block;
      }

      .wishlist-item__stock--in-stock {
        color: #2e7d32;
        background: #e8f5e9;
      }

      .wishlist-item__stock--low-stock {
        color: #e65100;
        background: #fff3e0;
      }

      .wishlist-item__stock--out-of-stock {
        color: #c62828;
        background: #ffebee;
      }

      .wishlist-item__share {
        display: flex;
        justify-content: center;
        gap: 8px;
        padding: 10px;
        border-top: 1px solid #f0f0f0;
      }

      .wishlist-share-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        cursor: pointer;
        transition: transform 0.2s ease, opacity 0.2s ease;
        text-decoration: none;
      }

      .wishlist-share-btn:hover {
        transform: scale(1.1);
        opacity: 0.85;
      }

      .wishlist-share-btn svg {
        width: 16px;
        height: 16px;
      }

      .wishlist-share-btn--whatsapp {
        background: #25d366;
        color: #fff;
      }

      .wishlist-share-btn--email {
        background: #bf9b30;
        color: #fff;
      }

      .wishlist-share-btn--facebook {
        background: #1877f2;
        color: #fff;
      }

      .wishlist-share-btn--twitter {
        background: #000;
        color: #fff;
      }

      .wishlist-share-btn--copy {
        background: #f0f0f0;
        color: #666;
      }

      .wishlist-share-btn--copy.copied {
        background: #4caf50;
        color: #fff;
      }

      .wishlist-item__cta {
        display: block;
        text-align: center;
        padding: 0.75rem;
        background: #1a1a1a;
        color: #fff;
        text-decoration: none;
        font-size: 0.75rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        transition: background 0.3s ease;
      }

      .wishlist-item__cta:hover {
        background: #bf9b30;
      }

      /* Empty state */
      .wishlist-empty {
        text-align: center;
        padding: 4rem 2rem;
      }

      .wishlist-empty__icon {
        width: 60px;
        height: 60px;
        color: #bf9b30;
        opacity: 0.5;
        margin-bottom: 1.5rem;
      }

      .wishlist-empty__title {
        font-family: var(--font-heading-family);
        font-size: 1.6rem;
        font-weight: 400;
        color: #1a1a1a;
        margin: 0 0 0.75rem;
      }

      .wishlist-empty__text {
        font-size: 1rem;
        color: #666;
        margin: 0 0 2rem;
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }

      .wishlist-empty__cta {
        display: inline-block;
        padding: 1rem 2.5rem;
        background: linear-gradient(135deg, #d4af37 0%, #8b6914 100%);
        color: #fff;
        text-decoration: none;
        font-size: 0.85rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        border-radius: 4px;
        transition: opacity 0.3s ease;
      }

      .wishlist-empty__cta:hover {
        opacity: 0.9;
      }

      /* Header wishlist count */
      .wishlist-count {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        background: #bf9b30;
        color: #fff;
        font-size: 10px;
        font-weight: 600;
        line-height: 16px;
        text-align: center;
        border-radius: 8px;
      }
    `;
    document.head.appendChild(style);
  }

  // Initialize
  function init() {
    injectStyles();
    updateWishlistCount();

    // Inject hearts on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        injectHeartButtons();
        injectProductPageHeart();
        renderWishlistPage();
        loginRun = syncWishlistOnLogin();
      });
    } else {
      injectHeartButtons();
      injectProductPageHeart();
      renderWishlistPage();
      loginRun = syncWishlistOnLogin();
    }

    // Watch for dynamically loaded cards (infinite scroll, AJAX)
    const observer = new MutationObserver((mutations) => {
      let hasNewCards = false;
      mutations.forEach(m => {
        m.addedNodes.forEach(n => {
          if (n.nodeType === 1 && (n.classList?.contains('card-wrapper') || n.querySelector?.('.card-wrapper'))) {
            hasNewCards = true;
          }
        });
      });
      if (hasNewCards) {
        setTimeout(injectHeartButtons, 100);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Expose API globally
  window.PCWishlist = {
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    get: getLocalWishlist,
    isIn: isInWishlist,
    isConfirmedIn,
    get ready() { return loginRun; },
    idle: async function() {
      await loginRun;
      await readTail;
      while ([...serverSync.values()].some(s => s.run)) await queueTail;
    },
    sync: syncWishlistOnLogin,
    render: renderWishlistPage
  };

  window.addEventListener('storage', e => {
    if (!e.key || e.key === OWNER_KEY || e.key === STORAGE_KEY || e.key.startsWith(INTENT_PREFIX)) {
      if (!currentOwner()) {
        identityStopped = true; localFallback = []; refreshWishlistUI();
      } else if (!pageCustomer.loggedIn) {
        try {
          const local = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
          if (validList(local)) localFallback = normalizeList(local);
        } catch (e) { storageUnavailable = true; }
        refreshWishlistUI();
      } else {
        restoreIntents(); syncWishlistOnLogin({ readOnly: true });
      }
    }
  });

  init();
})();
